import type { Activation } from '@growth/client';
import { useParams } from 'react-router';
import { BarList } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { ChartCard, ErrorBox, SampleBadge, StatTile } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtInt, fmtNum, fmtPct } from '../format.ts';
import { ciText, funnelItems, funnelStages } from '../metrics.ts';
import { useApi } from '../session.tsx';

export function ActivationPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const res = useApi<{ activation: Activation }>(`act:${gameId}:${filters.key}`, (api, s) => api.activation(gameId, filters.params, s));
  const a = res.data?.activation;
  const stale = res.loading && !!a;

  return (
    <>
      <PageHeader title="Activación" sub="Cohorte de nuevas instalaciones del periodo: qué hacen en sus primeras partidas y cuántas runs juegan antes de irse." />
      <FilterBar filters={filters} />
      {res.error && <ErrorBox error={res.error} onRetry={res.reload} />}
      {a && (
        <>
          <div className={`kpis loading-frame${stale ? ' stale' : ''}`}>
            <StatTile label="Instalaciones" value={fmtInt(a.installs)} sub={<SampleBadge sample={a.sample} />} />
            <StatTile label="Tutorial iniciado" value={fmtPct(a.tutorial_start_rate.rate)} sub={ciText(a.tutorial_start_rate)} />
            <StatTile label="Tutorial completado" value={fmtPct(a.tutorial_completion_rate.rate)} sub={`${fmtPct(a.tutorial_completion_of_starters.rate)} de quienes lo empiezan`} />
            <StatTile label="Primera run" value={fmtPct(a.first_run_start_rate.rate)} sub={`${fmtPct(a.first_run_completed_rate.rate)} la terminan`} />
            <StatTile label="Segunda run" value={fmtPct(a.second_run_rate.rate)} sub={ciText(a.second_run_rate)} />
            <StatTile label="Ven un rewarded" value={fmtPct(a.rewarded_ad_user_rate.rate)} sub={`runs hasta el primero: ${fmtNum(a.avg_runs_to_first_rewarded_ad, 1)} (mediana ${fmtNum(a.median_runs_to_first_rewarded_ad, 1)})`} />
            <StatTile label="Compradores" value={fmtPct(a.payer_rate.rate)} sub={`runs hasta la 1.ª compra: ${fmtNum(a.avg_runs_to_first_purchase, 1)} (mediana ${fmtNum(a.median_runs_to_first_purchase, 1)})`} />
            <StatTile label={`Abandono (${a.churn.churn_days} días sin jugar)`} value={fmtInt(a.churn.churned)} sub={`runs antes de irse: ${fmtNum(a.churn.avg_runs_until_churn, 1)} (mediana ${fmtNum(a.churn.median_runs_until_churn, 1)})`} />
          </div>
          <ChartCard
            title="Embudo de activación"
            subtitle="Porcentaje sobre las instalaciones de la cohorte · pasa el cursor para ver el IC 95 %"
            stale={stale}
            table={{
              columns: [{ label: 'Etapa' }, { label: 'Jugadores', numeric: true }, { label: '% instalaciones', numeric: true }, { label: 'IC 95 %', numeric: true }],
              rows: funnelStages(a).map((s) => [s.label, fmtInt(s.count), s.rate ? fmtPct(s.rate.rate) : '100 %', s.rate ? `${fmtPct(s.rate.ci_low)}–${fmtPct(s.rate.ci_high)}` : '—']),
            }}
          >
            <BarList ariaLabel="Embudo de activación" items={funnelItems(a)} format={fmtInt} />
          </ChartCard>
        </>
      )}
    </>
  );
}
